// 86d32t157 Phase 3 — mirror of backend
// CSC_CORE.Features.CuttingLists.Objects.DTOs.PricingDriftDetailDto. Surfaced
// to the client via the HTTP 409 PRICING_DRIFT response body.
import { isAxiosError } from 'axios';

export interface BeforeAfterDecimal {
  snapshot: number;
  current: number;
}

export interface BeforeAfterString {
  snapshot: string | null;
  current: string | null;
}

export interface FreightDriftDetail {
  /** Decimal field that drifted. Keys: "ratePerTon" | "flatRatePerTruck" |
   *  "tollPerTruck" | "discountPerTon" | "totalAmount". */
  numeric: Record<string, BeforeAfterDecimal>;
  /** Non-null when the saved freight `kind` doesn't match the recomputed one. */
  kindChanged: BeforeAfterString | null;
}

export interface TierClassificationDriftDetail {
  tier: BeforeAfterString | null;
  sizeMix: BeforeAfterString | null;
  adjustmentPct: BeforeAfterDecimal | null;
}

export interface LineDriftDetail {
  matchedItemId: string;
  finalUnitPrice: BeforeAfterDecimal | null;
  bracketId: BeforeAfterString | null;
}

export interface PricingDriftDetailsBlock {
  freight: FreightDriftDetail | null;
  baseMarkup: Record<string, BeforeAfterDecimal> | null;
  tierClassification: TierClassificationDriftDetail | null;
  lineDrift: LineDriftDetail[];
}

export interface PricingDriftDetail {
  /** Always `"PRICING_DRIFT"`. Sentinel the client switches on. */
  code: 'PRICING_DRIFT';
  /** Subset of `"base_markup" | "freight_rate" | "tier_classification" | "line_pricing"`. */
  drifted: string[];
  details: PricingDriftDetailsBlock;
  /** Signed change in cutting-list gross total (positive = customer charged more). */
  totalDelta: number;
}

/**
 * Typed error thrown by `quotationService.submitCuttingList` when the server
 * returns 409 PRICING_DRIFT. Panels catch this and open the drift modal — the
 * structured `detail` lets the modal render a precise before/after change list.
 */
export class PricingDriftError extends Error {
  readonly detail: PricingDriftDetail;
  constructor(detail: PricingDriftDetail) {
    super('Pricing has changed since this quotation was last saved.');
    this.name = 'PricingDriftError';
    this.detail = detail;
  }
}

/**
 * 86d3mnuxe FIX 5 — pull the structured drift detail out of a 409 PRICING_DRIFT response
 * (the ApiResponse envelope's `data` carries the DTO; `code` is the sentinel). Null for any
 * other error. Shared by the CL submit (two-option dialog) and the SO submit (refresh-only).
 */
export function extractPricingDrift(err: unknown): PricingDriftDetail | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  const body = err.response.data as { data?: PricingDriftDetail } | undefined;
  return body?.data?.code === 'PRICING_DRIFT' ? body.data : null;
}
